import { useEffect, useState } from "react";
import { BellRing, Clock, CheckCircle2, Ban, Loader2 } from "lucide-react";
import adminApi from "@/lib/adminApi";

const STATUS_STYLES = {
  pending: { icon: Clock, bg: "bg-amber-50 border-amber-200", text: "text-amber-800", chip: "bg-amber-100" },
  sent: { icon: CheckCircle2, bg: "bg-emerald-50 border-emerald-200", text: "text-emerald-800", chip: "bg-emerald-100" },
  cancelled: { icon: Ban, bg: "bg-gray-50 border-gray-200", text: "text-gray-600", chip: "bg-gray-100" },
};

export default function AdminReminders() {
  const [data, setData] = useState({ reminders: [], counts: { pending: 0, sent: 0, cancelled: 0 } });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const q = filter ? `?status=${filter}&limit=200` : "?limit=200";
      const r = await adminApi.get(`/admin/reminders${q}`);
      setData(r.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [filter]);

  return (
    <div className="p-6 md:p-8 max-w-6xl mx-auto" data-testid="admin-reminders">
      <div className="mb-6">
        <h1 className="font-heading text-3xl font-bold flex items-center gap-2">
          <BellRing className="w-6 h-6 text-amber-600" /> Reminders
        </h1>
        <p className="text-sm text-kudi-ink/60 mt-1">Every personal reminder scheduled by a trader via chat or WhatsApp.</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        {[
          { key: "", label: "All", n: (data.counts.pending + data.counts.sent + data.counts.cancelled), tone: "bg-white text-kudi-ink border-border" },
          { key: "pending", label: "Pending", n: data.counts.pending, tone: "bg-amber-50 text-amber-800 border-amber-200" },
          { key: "sent", label: "Sent", n: data.counts.sent, tone: "bg-emerald-50 text-emerald-800 border-emerald-200" },
          { key: "cancelled", label: "Cancelled", n: data.counts.cancelled, tone: "bg-gray-50 text-gray-700 border-gray-200" },
        ].map((s) => (
          <button
            key={s.key || "all"} onClick={() => setFilter(s.key)}
            className={`p-3 rounded-xl border text-left transition ${s.tone} ${filter === s.key ? "ring-2 ring-offset-1 ring-amber-500" : "hover:opacity-90"}`}
            data-testid={`reminders-filter-${s.key || "all"}`}
          >
            <div className="text-xs opacity-80">{s.label}</div>
            <div className="text-2xl font-bold tabular-nums">{s.n}</div>
          </button>
        ))}
      </div>

      <div className="bg-white border border-border rounded-2xl overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-kudi-ink/40 flex items-center justify-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading reminders…
          </div>
        ) : data.reminders.length === 0 ? (
          <div className="p-8 text-center text-kudi-ink/50">No reminders yet.</div>
        ) : (
          <ul className="divide-y divide-border">
            {data.reminders.map((r) => {
              const s = STATUS_STYLES[r.status] || STATUS_STYLES.pending;
              const S = s.icon;
              const due = r.due_at ? new Date(r.due_at) : null;
              const overdue = r.status === "pending" && due && due < new Date();
              return (
                <li key={r.id} className={`p-4 flex items-start gap-3 ${overdue ? "bg-rose-50/50" : ""}`} data-testid={`reminder-row-${r.id}`}>
                  <div className={`w-8 h-8 rounded-lg ${s.chip} ${s.text} flex items-center justify-center shrink-0`}>
                    <S className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{r.task}</div>
                    <div className="text-[11px] text-kudi-ink/55 mt-0.5 flex items-center gap-2 flex-wrap">
                      <span className={`inline-block px-1.5 py-0.5 rounded font-semibold ${s.chip} ${s.text}`}>{r.status}</span>
                      <span>{r.user?.owner_name || r.user?.email || "—"}</span>
                      {due && (
                        <span className={overdue ? "text-rose-600 font-semibold" : ""}>
                          · Due {due.toLocaleString("en-NG", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                          {overdue && " · OVERDUE"}
                        </span>
                      )}
                      {r.hint_used && <span className="font-mono text-kudi-ink/40">&quot;{r.hint_used}&quot;</span>}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
